import { z } from 'zod';

import { isConsumerWorkEmail } from './businessEmailPolicy';
import { workEmailRequiredMessage } from './competitorReportRequestFormMessages';

const requiredWorkEmailMessage = 'Please enter your work email address.';
const invalidWorkEmailMessage = 'Please enter a valid work email address.';

const hasValidWorkEmailSyntaxBeforeNormalization = (value: string) => {
  const separatorIndex = value.lastIndexOf('@');
  const containsWhitespaceOrControlCharacter = Array.from(value).some(character => {
    const codePoint = character.codePointAt(0);

    return (
      character.trim().length === 0 ||
      codePoint === undefined ||
      codePoint <= 0x1f ||
      codePoint === 0x7f
    );
  });

  return (
    separatorIndex > 0 &&
    separatorIndex === value.indexOf('@') &&
    separatorIndex < value.length - 1 &&
    !containsWhitespaceOrControlCharacter
  );
};

const normalizeWorkEmail = (value: string) => {
  const separatorIndex = value.lastIndexOf('@');
  const localPart = value.slice(0, separatorIndex);
  const domain = value.slice(separatorIndex + 1);

  if (separatorIndex <= 0 || !domain) return value;
  if (/[%:/\\?#]/.test(domain)) return value;

  try {
    const domainUrl = new URL(`http://${domain}`);

    if (
      domainUrl.username ||
      domainUrl.password ||
      domainUrl.port ||
      domainUrl.pathname !== '/' ||
      domainUrl.search ||
      domainUrl.hash
    ) {
      return value;
    }

    return `${localPart}@${domainUrl.hostname}`;
  } catch {
    return value;
  }
};

const normalizeCompetitorWebsites = (value: string) => value.replace(/\r\n?/g, '\n').trim();

const workEmailSyntaxSchema = z
  .string({ required_error: requiredWorkEmailMessage })
  .trim()
  .min(1, { message: requiredWorkEmailMessage })
  .max(254, { message: invalidWorkEmailMessage })
  .refine(hasValidWorkEmailSyntaxBeforeNormalization, {
    message: invalidWorkEmailMessage,
  })
  .transform(normalizeWorkEmail)
  .pipe(
    z
      .string()
      .max(254, { message: invalidWorkEmailMessage })
      .email({ message: invalidWorkEmailMessage }),
  );

const competitorReportRequestBaseSchema = z.object({
  email: workEmailSyntaxSchema,
  companyName: z
    .string({ required_error: 'Please enter your company name.' })
    .trim()
    .min(1, { message: 'Please enter your company name.' })
    .max(200, { message: 'Company name must be 200 characters or fewer.' }),
  competitorWebsites: z
    .string()
    .default('')
    .transform(normalizeCompetitorWebsites)
    .pipe(
      z.string().max(4000, {
        message: 'Competitor websites must be 4,000 characters or fewer.',
      }),
    ),
  processingConsent: z.boolean().refine(value => value, {
    message: 'Please agree to the processing of your details.',
  }),
});

export const competitorReportRequestFormSchema = competitorReportRequestBaseSchema.extend({
  email: workEmailSyntaxSchema.refine(workEmail => !isConsumerWorkEmail(workEmail), {
    message: workEmailRequiredMessage,
  }),
});

export const competitorReportRequestPreflightActionSchema =
  competitorReportRequestBaseSchema.extend({
    sourceUrl: z.string().url(),
  });

export const competitorReportRequestActionSchema =
  competitorReportRequestPreflightActionSchema.extend({
    gRecaptchaToken: z.string().min(1),
  });

export type CompetitorReportRequestFormValues = z.infer<typeof competitorReportRequestFormSchema>;
